// Output paths (Copy / Download / View) + size meter for the Email
// editor screen. Per spec/build-plan.md P-033 row + tech-arch.md §9 +
// §9.1. First non-preview consumer of `renderEmail` (D-091).
//
// **Light-only export** (D-100, P-037 close). Earlier `colorScheme`
// branching for the preview-iframe path is gone — the entire dark-mode
// shell + toggle were removed alongside.
//
// **Toast UX (P-035 / D-###).** Replaces the P-033 inline status seam
// (D-095 — `Status` discriminated union + `<span role="status">` +
// email-state-change clear effect) with `useToast()` from
// `src/app/toast/`. Success / failure surfaces fire success / error
// toasts respectively; the auto-dismiss timer + manual-dismiss button
// live on the `<ToastContainer>` mount in `src/app/app.tsx`. The
// inline status seam was forward-compatible with this swap by
// design (D-095 forward note).
//
// **Output toolbar placement (Banks Ask 2 → option b).** Separate
// `<OutputToolbar>` sibling mounted below the screen header in
// email-editor.tsx — keeps `<PreviewToolbar>` focused on preview-
// display state.
//
// **Filename format (Banks Ask 3 → build-plan default).**
// `{slug(email.name)}-{YYYY-MM-DD}.html` per Tech Arch §9.
//
// **Independent `renderEmail` compute, debounced.** Output toolbar
// runs its own `useMemo` walk over `useDebounced(email, 50ms)`,
// matching `<PreviewIframe>`'s debounce window so size-meter updates
// stay in lockstep with the preview surface and the per-keystroke
// renderEmail cost is rate-limited. Share-and-lift deferred per the
// OC §3.5 measurement-driven gate.
//
// **Blob URL lifecycle.**
// - Download: routes through the shared `downloadBlob` helper
//   (`src/lib/download.ts`) — hoisted at the 3rd consumer (P-034
//   JSON export) per the project's 3-use threshold.
// - View-in-Tab: keeps its own flow because the new tab needs the
//   URL alive while it loads. `setTimeout(60_000)` cleanup gives a
//   generous window — rendered output is 5–50 KB per Tech Arch §9.1
//   and staff sessions are bounded to a few hours.
//
// **Clipboard failure path.** No deprecated
// `document.execCommand('copy')` fallback — modern browsers in the
// staff editor's compatibility target all support
// `navigator.clipboard`. Failure surfaces as an error toast.

import { useMemo } from 'preact/hooks'

import { downloadBlob } from '../../lib/download'
import { errorMessage } from '../../lib/error'
import { filenameSlug } from '../../lib/slug'
import { renderEmail } from '../../render/render-email'
import type { Email } from '../../store/schemas'
import { secondaryButtonClass } from '../button-classes'
import { useDebounced } from '../hooks/use-debounced'
import { useRovingTabIndex } from '../hooks/use-roving-tabindex'
import { useToast } from '../toast/use-toast'
import { PushButton } from './email-editor-push-button'

const SOFT_WARN_BYTES = 80 * 1024
const HARD_WARN_BYTES = 100 * 1024

const DEBOUNCE_MS = 50
const VIEW_TAB_BLOB_TTL_MS = 60_000

const BADGE_BASE = 'inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium'
const BADGE_OK = `${BADGE_BASE} border-gray-200 bg-gray-50 text-gray-700`
const BADGE_SOFT = `${BADGE_BASE} border-amber-300 bg-amber-50 text-amber-800`
const BADGE_HARD = `${BADGE_BASE} border-red-300 bg-red-50 text-red-800`

interface OutputToolbarProps {
  readonly email: Email
}

interface SizeBadge {
  readonly className: string
  readonly label: string
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10)
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  return `${(n / 1024).toFixed(1)} KB`
}

function sizeBadge(bytes: number): SizeBadge {
  if (bytes > HARD_WARN_BYTES) {
    return { className: BADGE_HARD, label: `${formatBytes(bytes)} · may be clipped by Gmail` }
  }
  if (bytes > SOFT_WARN_BYTES) {
    return { className: BADGE_SOFT, label: `${formatBytes(bytes)} · approaching Gmail limit` }
  }
  return { className: BADGE_OK, label: formatBytes(bytes) }
}

function openHtmlInTab(html: string) {
  const blob = new Blob([html], { type: 'text/html' })
  const url = URL.createObjectURL(blob)
  window.open(url, '_blank', 'noopener,noreferrer')
  setTimeout(() => {
    URL.revokeObjectURL(url)
  }, VIEW_TAB_BLOB_TTL_MS)
}

type RenderResult =
  | { readonly ok: true; readonly html: string; readonly bytes: number }
  | { readonly ok: false; readonly error: string }

export function OutputToolbar({ email }: OutputToolbarProps) {
  const { addToast } = useToast()
  const debouncedEmail = useDebounced(email, DEBOUNCE_MS)
  const { rootRef, activeIndex, setActiveIndex } = useRovingTabIndex<HTMLDivElement>()

  const result: RenderResult = useMemo(() => {
    try {
      const html = renderEmail(debouncedEmail)
      return { ok: true, html, bytes: new Blob([html]).size }
    } catch (e) {
      return { ok: false, error: errorMessage(e) }
    }
  }, [debouncedEmail])

  if (!result.ok) {
    return (
      <div
        role="toolbar"
        aria-label="Output controls"
        class="flex flex-wrap items-center gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-900"
      >
        Output unavailable — render error: {result.error}
      </div>
    )
  }

  const { html, bytes } = result
  const badge = sizeBadge(bytes)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(html)
      addToast({ kind: 'success', message: 'Copied to clipboard' })
    } catch (e) {
      addToast({ kind: 'error', message: errorMessage(e, 'Copy failed') })
    }
  }

  const handleDownload = () => {
    try {
      downloadBlob(html, `${filenameSlug(email.name, 'email')}-${todayIso()}.html`, 'text/html')
      addToast({ kind: 'success', message: 'Downloaded' })
    } catch (e) {
      addToast({ kind: 'error', message: errorMessage(e, 'Download failed') })
    }
  }

  const handleView = () => {
    try {
      openHtmlInTab(html)
      addToast({ kind: 'success', message: 'Opened in new tab' })
    } catch (e) {
      addToast({ kind: 'error', message: errorMessage(e, 'Could not open tab') })
    }
  }

  return (
    <div
      ref={rootRef}
      role="toolbar"
      aria-label="Output controls"
      class="flex flex-wrap items-center gap-2 rounded border border-gray-200 bg-white px-3 py-2"
    >
      <span class={badge.className} aria-label="Output size">
        {badge.label}
      </span>
      <button
        type="button"
        tabIndex={activeIndex === 0 ? 0 : -1}
        onFocus={() => setActiveIndex(0)}
        onClick={handleCopy}
        class={secondaryButtonClass}
      >
        Copy HTML
      </button>
      <button
        type="button"
        tabIndex={activeIndex === 1 ? 0 : -1}
        onFocus={() => setActiveIndex(1)}
        onClick={handleDownload}
        class={secondaryButtonClass}
      >
        Download HTML
      </button>
      <button
        type="button"
        tabIndex={activeIndex === 2 ? 0 : -1}
        onFocus={() => setActiveIndex(2)}
        onClick={handleView}
        class={secondaryButtonClass}
      >
        View in Tab
      </button>
      <PushButton
        email={email}
        tabIndex={activeIndex === 3 ? 0 : -1}
        onFocus={() => setActiveIndex(3)}
      />
    </div>
  )
}
